import { useCallback, useEffect, useState } from 'react';
import { integrationService } from '@/services';
import type { IntercomTeammate } from '@/services/intercom-integration.service';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';

const SAVE_FAILURE_MESSAGE = 'Could not save the teammate. Please try again.';

export interface UseIntercomKimTeammateResult {
	/** The workspace's teammates; empty until loaded, and when the workspace has none to pick. */
	teammates: IntercomTeammate[];
	/** The teammate Kim already works as; null when none has been picked. */
	savedTeammateId: string | null;
	isLoading: boolean;
	loadFailed: boolean;
	isSaving: boolean;
	/** The reason the last save failed, shown inline; null when nothing failed. */
	errorMessage: string | null;
	save: (teammateId: string) => Promise<void>;
}

/** The teammate Kim works as in the connected Intercom workspace: load the choices, save the pick. */
export const useIntercomKimTeammate = (): UseIntercomKimTeammateResult => {
	const [teammates, setTeammates] = useState<IntercomTeammate[]>([]);
	const [savedTeammateId, setSavedTeammateId] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		let isMounted = true;
		integrationService
			.getIntercomTeammates()
			.then(({ teammates: loaded, selectedId }) => {
				if (!isMounted) return;
				setTeammates(loaded);
				setSavedTeammateId(selectedId);
			})
			.catch((err: unknown) => {
				console.error('[useIntercomKimTeammate] teammates request failed', { err });
				if (isMounted) setLoadFailed(true);
			})
			.finally(() => {
				if (isMounted) setIsLoading(false);
			});
		return () => {
			isMounted = false;
		};
	}, []);

	const save = useCallback(async (teammateId: string) => {
		setIsSaving(true);
		setErrorMessage(null);
		await integrationService
			.selectIntercomKimTeammate(teammateId)
			.then((teammate) => {
				setSavedTeammateId(teammate.id);
				toast.success(`Kim now works as ${teammate.name}`);
			})
			.catch((err: unknown) => {
				console.error('[useIntercomKimTeammate.save] select request failed', { teammateId, err });
				setErrorMessage(getApiErrorMessage(err, SAVE_FAILURE_MESSAGE));
			});
		setIsSaving(false);
	}, []);

	return { teammates, savedTeammateId, isLoading, loadFailed, isSaving, errorMessage, save };
};
